"use client";

import { type ReactNode, useEffect, useState } from "react";
import {
  type CipherMode,
  CipherWorkbench,
  SymbolChart,
} from "@/components/tool-kit/cipher-workbench";
import {
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import {
  T9Picker,
  type T9Word,
} from "@/components/tools/ciphers-misc/t9-picker";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  decodeKeypad,
  encodeKeypad,
  KEYPAD_KEYS,
  type KeypadMethod,
  t9Words,
  type WordBreak,
} from "@/lib/tool-kit/ciphers-misc/phone-keypad";
import { loadT9Index } from "@/lib/tool-kit/ciphers-misc/t9-dictionary";

type Status = "idle" | "loading" | "ready" | "failed";

const ROWS_SHOWN = 24;
const CANDIDATES_SHOWN = 8;

/** English strings. A translated content file overrides these through `ui`. */
export const PHONE_KEYPAD_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Keypad digits",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  cipherPlaceholder: "For example 44 33 555 555 666",
  sample: "Hello world",
  method: "Method",
  multitap: "Multi-tap (press a key repeatedly: C is 222)",
  pairs: "Key and press count (C is 2-3)",
  t9: "T9 digits (one digit per letter, ambiguous)",
  wordBreak: "Write the space between words as",
  breakZero: "0 (the space key on most phones)",
  breakSlash: "/ (a slash)",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  ignored: "Left out because they are not letters or spaces: {items}",
  invalid: "These do not name a letter and are shown as ?: {items}",
  invalidDigits: "These are not T9 digits (2–9): {items}",
  working: "The keypad",
  keypadLabel: "Telephone keypad letters",
  noLetters: "no letters",
  workingEncode:
    "Each letter is found on the keypad and written as its key, with the number of presses it takes to reach it.",
  workingDecode:
    "Each group of repeated digits names a key and a count; the count picks the letter on that key.",
  colLetter: "Letter",
  colKey: "Key",
  colPresses: "Presses",
  colWritten: "Written",
  colToken: "Written",
  rowsShown:
    "one: Showing the first row.|other: Showing the first {count} rows.",
  emptyWorking: "Type a message to see the working.",
  t9Heading: "Choose the words",
  t9Hint:
    "The same digits fit several words. The commonest comes first; pick another if the first is not what was meant.",
  t9NoMatch: "No word in the dictionary fits these digits.",
  t9Word: "Word {n}",
  t9Loading: "Loading the dictionary…",
  t9Failed: "The dictionary could not be loaded.",
  retry: "Try again",
  t9Note:
    "T9 digits cannot be decoded exactly, because several words share each digit string. Candidates come from a word list ranked by how common each word is.",
} as const;

type Strings = typeof PHONE_KEYPAD_CIPHER_STRINGS;

interface Settings {
  mode: CipherMode;
  method: KeypadMethod;
  wordBreak: WordBreak;
}

interface Dictionary {
  status: Status;
  index: Map<string, string[]> | null;
}

function sampleFor(strings: Strings, settings: Settings): string {
  if (settings.mode === "encode") {
    return strings.sample;
  }
  return encodeKeypad(strings.sample, settings.method, settings.wordBreak)
    .output;
}

/** The T9 dictionary, loaded the first time `enabled` is true. */
function useT9Dictionary(enabled: boolean): {
  dictionary: Dictionary;
  retry: () => void;
} {
  const [dictionary, setDictionary] = useState<Dictionary>({
    status: "idle",
    index: null,
  });
  useEffect(() => {
    if (!enabled || dictionary.status !== "idle") {
      return;
    }
    setDictionary({ status: "loading", index: null });
    loadT9Index()
      .then((index) => setDictionary({ status: "ready", index }))
      .catch(() => setDictionary({ status: "failed", index: null }));
  }, [enabled, dictionary.status]);
  return {
    dictionary,
    retry: () => setDictionary({ status: "idle", index: null }),
  };
}

interface View {
  output: string;
  /** Rows of the working table. */
  rows: string[][];
  /** Keys of the keypad the message touches. */
  active: Set<string>;
  ignored: string[];
  invalid: string[];
  picker: T9Word[];
}

function t9View(
  input: string,
  index: Map<string, string[]> | null,
  choices: Record<number, number>
): View {
  const typed = t9Words(input);
  const picker: T9Word[] = typed.words.map((code, position) => {
    const candidates = index?.get(code)?.slice(0, CANDIDATES_SHOWN) ?? [];
    const chosen = Math.min(choices[position] ?? 0, candidates.length - 1);
    return { code, candidates, chosen: Math.max(0, chosen) };
  });
  const output = picker
    .map((word) => word.candidates[word.chosen] ?? "?".repeat(word.code.length))
    .join(" ");
  const active = new Set(typed.words.flatMap((code) => [...code]));
  return {
    output,
    rows: [],
    active,
    ignored: [],
    invalid: typed.invalid,
    picker,
  };
}

function view(
  input: string,
  settings: Settings,
  fmt: (value: number) => string,
  index: Map<string, string[]> | null,
  choices: Record<number, number>
): View {
  const { mode, method, wordBreak } = settings;
  if (mode === "encode") {
    const encoded = encodeKeypad(input, method, wordBreak);
    return {
      output: encoded.output,
      rows: encoded.rows.map((row) => [
        row.letter,
        row.key,
        fmt(row.presses),
        row.code,
      ]),
      active: new Set(encoded.rows.map((row) => row.key)),
      ignored: encoded.ignored,
      invalid: [],
      picker: [],
    };
  }
  if (method === "t9") {
    return t9View(input, index, choices);
  }
  const decoded = decodeKeypad(input, method);
  const letters = decoded.tokens.filter((token) => !token.isBreak);
  return {
    output: decoded.output,
    rows: letters.map((token) => [
      token.token,
      token.letter ? token.token[0] : "?",
      token.letter ? fmt(token.token.length) : "?",
      token.letter ?? "?",
    ]),
    active: new Set(
      letters.filter((token) => token.letter).map((token) => token.token[0])
    ),
    ignored: [],
    invalid: decoded.invalid,
    picker: [],
  };
}

function KeypadWorking({
  strings,
  encoding,
  t9Decode,
  rows,
  active,
}: {
  strings: Strings;
  encoding: boolean;
  t9Decode: boolean;
  rows: string[][];
  active: Set<string>;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  let table: ReactNode = null;
  if (!t9Decode) {
    table =
      rows.length > 0 ? (
        <div className="space-y-3">
          <p className="text-muted-foreground text-sm leading-relaxed">
            {encoding ? strings.workingEncode : strings.workingDecode}
          </p>
          <DataTable
            head={[
              encoding ? strings.colLetter : strings.colToken,
              strings.colKey,
              strings.colPresses,
              encoding ? strings.colWritten : strings.colLetter,
            ]}
            rows={rows.slice(0, ROWS_SHOWN)}
          />
          {rows.length > ROWS_SHOWN ? (
            <p className={HINT}>
              {tPlural(strings.rowsShown, locale, ROWS_SHOWN, {
                count: fmt(ROWS_SHOWN),
              })}
            </p>
          ) : null}
        </div>
      ) : (
        <Notice>{strings.emptyWorking}</Notice>
      );
  }
  return (
    <div className="space-y-4">
      <SymbolChart
        items={KEYPAD_KEYS.map((entry) => ({
          key: entry.key,
          value: entry.letters || strings.noLetters,
          active: active.has(entry.key),
        }))}
        label={strings.keypadLabel}
      />
      {table}
    </div>
  );
}

function KeypadNotices({
  strings,
  current,
  t9Decode,
  status,
  onRetry,
}: {
  strings: Strings;
  current: View;
  t9Decode: boolean;
  status: Status;
  onRetry: () => void;
}) {
  const invalidText = t9Decode ? strings.invalidDigits : strings.invalid;
  return (
    <div className="space-y-2">
      {current.ignored.length > 0 ? (
        <Notice>
          {t(strings.ignored, { items: current.ignored.join(" ") })}
        </Notice>
      ) : null}
      {current.invalid.length > 0 ? (
        <ErrorNotice>
          {t(invalidText, { items: current.invalid.join(" ") })}
        </ErrorNotice>
      ) : null}
      {t9Decode ? <p className={HINT}>{strings.t9Note}</p> : null}
      {t9Decode && status === "loading" ? (
        <Notice>{strings.t9Loading}</Notice>
      ) : null}
      {t9Decode && status === "failed" ? (
        <div className="flex flex-wrap items-center gap-3">
          <ErrorNotice>{strings.t9Failed}</ErrorNotice>
          <button className={BUTTON_SECONDARY} onClick={onRetry} type="button">
            {strings.retry}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function PhoneKeypadCipherWidget() {
  const strings = useToolUi(PHONE_KEYPAD_CIPHER_STRINGS);
  const fmt = useToolNumber();
  const [settings, setSettings] = useState<Settings>({
    mode: "encode",
    method: "multitap",
    wordBreak: "0",
  });
  const [input, setInput] = useState<string>(strings.sample);
  const [choices, setChoices] = useState<Record<number, number>>({});

  const { mode, method, wordBreak } = settings;
  const encoding = mode === "encode";
  const t9Decode = !encoding && method === "t9";
  const { dictionary, retry } = useT9Dictionary(t9Decode);
  const current = view(input, settings, fmt, dictionary.index, choices);

  function update(next: Partial<Settings>) {
    const merged = { ...settings, ...next };
    if (input === sampleFor(strings, settings)) {
      setInput(sampleFor(strings, merged));
    }
    setChoices({});
    setSettings(merged);
  }

  function changeInput(value: string) {
    setChoices({});
    setInput(value);
  }

  const keyControls = (
    <div className="grid gap-4 sm:grid-cols-2">
      <SelectField
        id="pk-method"
        label={strings.method}
        onChange={(value) => update({ method: value })}
        options={[
          { value: "multitap", label: strings.multitap },
          { value: "pairs", label: strings.pairs },
          { value: "t9", label: strings.t9 },
        ]}
        value={method}
      />
      {encoding ? (
        <SelectField
          id="pk-break"
          label={strings.wordBreak}
          onChange={(value) => update({ wordBreak: value })}
          options={[
            { value: "0", label: strings.breakZero },
            { value: "/", label: strings.breakSlash },
          ]}
          value={wordBreak}
        />
      ) : null}
    </div>
  );

  const extras = t9Decode ? (
    <T9Picker
      labels={{
        heading: strings.t9Heading,
        hint: strings.t9Hint,
        noMatch: strings.t9NoMatch,
        wordLabel: (n) => t(strings.t9Word, { n: fmt(n) }),
      }}
      onChoose={(word, candidate) =>
        setChoices((existing) => ({ ...existing, [word]: candidate }))
      }
      words={current.picker}
    />
  ) : null;

  return (
    <CipherWorkbench
      extras={extras}
      idPrefix="pk"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: encoding ? strings.plainText : strings.cipherText,
        inputPlaceholder: encoding
          ? strings.inputPlaceholder
          : strings.cipherPlaceholder,
        output: encoding ? strings.cipherText : strings.decodedText,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={
        <KeypadNotices
          current={current}
          onRetry={retry}
          status={dictionary.status}
          strings={strings}
          t9Decode={t9Decode}
        />
      }
      onInputChange={changeInput}
      onModeChange={(next) => update({ mode: next })}
      output={current.output}
      working={
        <KeypadWorking
          active={current.active}
          encoding={encoding}
          rows={current.rows}
          strings={strings}
          t9Decode={t9Decode}
        />
      }
    />
  );
}
